import { ProjectMapping } from '@/types/project-mappings'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<ProjectMapping>({
  pathCategory: 'project_mappings',
})

export function useGetProjectMapping({
  id,
  ...options
}: UseGetDetailOptions<ProjectMapping>) {
  const query = useGetDetail<ProjectMapping>({
    id,
    queryKey: queryKeys.projectMappings.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    projectMapping: query.data,
    ...query,
  }
}

export function useCreateProjectMapping(
  options: UseCreateDetailOptions<ProjectMapping> = {},
) {
  const mutation = useCreateDetail<ProjectMapping>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createProjectMapping: mutation.mutate,
    createProjectMappingAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateProjectMapping(
  options: UseUpdateDetailOptions<ProjectMapping> = {},
) {
  const mutation = useUpdateDetail<ProjectMapping>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateProjectMapping: mutation.mutate,
    updateProjectMappingAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteProjectMapping(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<ProjectMapping>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteProjectMapping: mutation.mutate,
    deleteProjectMappingAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetProjectMappings({
  filters,
  ...options
}: UseGetListOptions<ProjectMapping> = {}) {
  const query = useGetList<ProjectMapping>({
    filters,
    queryKey: queryKeys.projectMappings.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    projectMappings: query.allData,
    ...query,
  }
}

export function useUpdateProjectMappings(
  options: UseUpdateListOptions<ProjectMapping> = {},
) {
  const mutation = useUpdateList<ProjectMapping>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateProjectMappings: mutation.mutate,
    updateProjectMappingsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteProjectMappings({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<ProjectMapping>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteProjectMappings: mutation.mutate,
    deleteProjectMappingsAsync: mutation.mutateAsync,
    ...mutation,
  }
}
